import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Totals from "../Totals";
import Ticket from "../Ticket";
import SelfTicket from "../SelfTicket";
import Button from "../Button";
import { clearTicketState } from "../../utils/ticketStorage";

const PLACEHOLDER = "—";
const ADD_TO_CART_TIMEOUT_MS = 20_000;
const CART_REDIRECT_COUNTDOWN_SECONDS = 3;

/**
 * A ticket counts as complete once it identifies a person.
 *
 * The name test alone dropped anyone whose account has no first or last name: the
 * membership lookup returned blanks, the placeholder stayed, and the ticket was
 * removed from the payload without a message. An email or a user id is enough.
 */
function isCompletedTicket(t) {
  if (t.first_name !== PLACEHOLDER || t.last_name !== PLACEHOLDER) return true;
  if (t.user_id != null && t.user_id !== "") return true;
  return typeof t.email === "string" && t.email.trim() !== "";
}

function createPlaceholderTicket({ selfPromptAnswered = false } = {}) {
  return {
    id: `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    first_name: "—",
    last_name: "—",
    member_type: "pending",
    price: null,
    showBody: true,
    selfPromptAnswered,
  };
}

function createSelfTicket(data) {
  const u = data?.user;
  const level = u?.user_membership_level || "none";
  const memberType = level === "none" ? "non-member" : level;
  const price = typeof u?.cost === "string" || typeof u?.cost === "number"
    ? Number(u.cost)
    : Number(data?.prices?.non_member ?? 0);
  return {
    id: "self-ticket",
    isSelf: true,
    first_name: u?.first_name ?? "",
    last_name: u?.last_name ?? "",
    member_type: memberType,
    price,
    user_id: u?.uid ?? null,
    company: u?.company ?? "",
    title: u?.title ?? "",
    email: u?.email ?? "",
    showBody: false,
  };
}

function hasSelfTicket(tickets, data) {
  return tickets.some(
    (t) =>
      t.isSelf ||
      (data?.user?.uid != null &&
        t.user_id != null &&
        String(t.user_id) === String(data.user.uid))
  );
}

function hasDuplicateEmail(tickets, email, excludeIndex) {
  if (!email || typeof email !== "string") return false;
  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;
  return tickets.some(
    (t, i) => i !== excludeIndex && (t.email ?? "").trim().toLowerCase() === normalized
  );
}

export default function PurchaseForm({
  data,
  initialFormState,
  onFormStateChange,
  onAddToCartSuccess,
}) {
  const {
    event_id,
    product_id,
    cart_url,
    user,
    prices,
    membership_status_url,
    add_to_cart_api_url,
  } = data ?? {};
  const [isForYou, setIsForYou] = useState(initialFormState?.isForYou ?? 0);
  const [tickets, setTickets] = useState(initialFormState?.tickets ?? []);
  const [total, setTotal] = useState(0);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [addToCartRedirecting, setAddToCartRedirecting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const [addToCartError, setAddToCartError] = useState(null);
  const [openTicketIndex, setOpenTicketIndex] = useState(null);

  // Use a ref so the `finally` block always sees current redirecting state.
  const redirectingRef = useRef(false);
  const redirectUrlRef = useRef(null);
  const redirectTimerRef = useRef(null);

  const completedTickets = tickets.filter(isCompletedTicket);
  const hasCompletedTicket = completedTickets.length > 0;
  const selfTicketPresent = hasSelfTicket(tickets, data);

  // When there are no tickets, always show the self-ticket check.
  useEffect(() => {
    if (tickets.length === 0 && isForYou !== 0) {
      setIsForYou(0);
    }
  }, [tickets.length, isForYou]);

  // Restore open ticket for placeholder when loading from storage.
  useEffect(() => {
    if (openTicketIndex == null && isForYou > 0) {
      const idx = tickets.findIndex(
        (t) => !t.isSelf && t.first_name === PLACEHOLDER && t.last_name === PLACEHOLDER
      );
      if (idx >= 0) setOpenTicketIndex(idx);
    }
  }, []);

  const handleAddTicket = () => {
    const newIndex = tickets.length;
    setTickets((prev) => [...prev, createPlaceholderTicket()]);
    setOpenTicketIndex(newIndex);
  };

  const handleSelfTicketChoice = (idx, isSelf) => {
    if (isSelf) {
      setIsForYou(1);
      setTickets((prevTickets) => {
        const next = [...prevTickets];
        const ticket = next[idx];
        if (!ticket) return prevTickets;
        const selfTicket = createSelfTicket(data);
        next[idx] = {
          ...selfTicket,
          id: ticket.id ?? selfTicket.id,
          selfPromptAnswered: true,
        };
        return next;
      });
      setOpenTicketIndex(null);
      return;
    }

    setTickets((prevTickets) => {
      const next = [...prevTickets];
      const ticket = next[idx];
      if (!ticket) return prevTickets;
      next[idx] = {
        ...ticket,
        selfPromptAnswered: true,
      };
      return next;
    });
  };

  const handleRemoveTicket = (idx) => {
    setTickets((prevTickets) => {
      const next = prevTickets.filter((_, index) => index !== idx);
      setOpenTicketIndex((prev) => {
        if (prev == null) return null;
        if (prev === idx) return null;
        if (prev > idx) return prev - 1;
        return prev;
      });
      return next;
    });
  };

  const handleMembershipFound = (idx, membershipData) => {
    const email = membershipData?.email ?? "";
    if (hasDuplicateEmail(tickets, email, idx)) {
      return false;
    }
    setTickets((prev) => {
      const next = [...prev];
      const t = next[idx];
      if (!t) return prev;
      next[idx] = {
        ...t,
        first_name: membershipData.first_name || t.first_name,
        last_name: membershipData.last_name || t.last_name,
        member_type: membershipData.membership_level || t.member_type,
        price: Number(membershipData.price ?? t.price),
        user_id: membershipData.user_id,
        company: membershipData.company ?? t.company ?? "",
        title: membershipData.title ?? t.title ?? "",
        email: membershipData.email,
      };
      return next;
    });
    return true;
  };

  const handleAddToCart = async () => {
    if (!add_to_cart_api_url) {
      setAddToCartError("Cart configuration is missing. Please refresh and try again.");
      return;
    }
    if (!product_id) {
      setAddToCartError("No product associated with this event.");
      return;
    }
    if (!hasCompletedTicket) return;

    setAddToCartLoading(true);
    setAddToCartError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ADD_TO_CART_TIMEOUT_MS);

    try {
      const ticketsToSend = tickets.filter(isCompletedTicket);

      // Tell the customer instead of quietly buying fewer tickets than they entered.
      const droppedCount = tickets.length - ticketsToSend.length;
      if (droppedCount > 0) {
        setAddToCartError(
          `${droppedCount} ticket${droppedCount > 1 ? "s are" : " is"} missing an email address. ` +
            "Complete or remove them, then try again.",
        );
        return; // the finally block clears the timeout and the loading state
      }

      const payload = {
        event_id,
        product_id,
        is_for_you: isForYou,
        // is_self marks the buyer's own ticket. The server must not assume it is the
        // first ticket in the list - the buyer can answer "this one is for me" on any
        // ticket, and this list is filtered, so positions shift.
        tickets: ticketsToSend.map((t) => ({
          first_name: t.first_name,
          last_name: t.last_name,
          member_type: t.member_type,
          price: t.price,
          user_id: t.user_id ?? null,
          company: t.company ?? "",
          title: t.title ?? "",
          email: t.email ?? "",
          is_self: t.isSelf ? 1 : 0,
        })),
      };
      const headers = { "Content-Type": "application/json" };
      const nonce = window.wpApiSettings?.nonce;
      if (nonce) headers["X-WP-Nonce"] = nonce;

      const res = await axios.post(add_to_cart_api_url, payload, {
        withCredentials: true,
        headers,
        signal: controller.signal,
      });

      const url = res.data?.cart_url;
      if (!url) {
        throw new Error("Server did not return a cart URL. Please try again.");
      }

      redirectingRef.current = true;
      redirectUrlRef.current = url;
      setAddToCartLoading(false);
      setAddToCartRedirecting(true);
      setRedirectCountdown(CART_REDIRECT_COUNTDOWN_SECONDS);
      redirectTimerRef.current = window.setTimeout(() => {
        window.location.href = url;
      }, CART_REDIRECT_COUNTDOWN_SECONDS * 1000);
      clearTicketState(event_id);
      onAddToCartSuccess?.();
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") {
        setAddToCartError("The request timed out. Please try again.");
      } else if (!navigator.onLine) {
        setAddToCartError("You appear to be offline. Please check your connection.");
      } else {
        const status = err.response?.status;
        let msg;
        if (status === 409) {
          msg = err.response?.data?.message || "This item may already be in your cart.";
        } else if (status >= 500) {
          msg = "Server error — please try again later.";
        } else {
          msg = err.response?.data?.message || err.message || "Could not add to cart.";
        }
        setAddToCartError(msg);
      }
    } finally {
      clearTimeout(timeoutId);
      if (!redirectingRef.current) {
        setAddToCartLoading(false);
      }
    }
  };

  useEffect(() => {
    if (redirectCountdown == null) return;
    if (redirectCountdown <= 0) return;
    const timer = setTimeout(() => setRedirectCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirectCountdown]);

  useEffect(() => {
    if (onFormStateChange) onFormStateChange({ isForYou, tickets });
  }, [isForYou, tickets, onFormStateChange]);

  useEffect(() => {
    const cost = tickets.reduce((sum, ticket) => sum + (Number(ticket.price) || 0), 0);
    setTotal(cost);
  }, [tickets]);

  return (
    <div className="form">
      <div className="tickets__grid">
        {isForYou === 0 ? (
          <SelfTicket
            data={data}
            setTickets={setTickets}
            setIsForYou={setIsForYou}
            onNotForYou={() => {
              setIsForYou(2);
              setTickets([createPlaceholderTicket({ selfPromptAnswered: true })]);
              setOpenTicketIndex(0);
            }}
          />
        ) : (
          <>
            {tickets.map((ticket, index) => (
              <Ticket
                key={ticket.id || index}
                firstName={ticket.first_name}
                lastName={ticket.last_name}
                memberType={ticket.member_type}
                price={ticket.price}
                handleRemoveTicket={handleRemoveTicket}
                index={index}
                eventId={event_id}
                membershipStatusUrl={membership_status_url}
                onMembershipFound={(membershipData) => {
                  const ok = handleMembershipFound(index, membershipData);
                  if (ok) setOpenTicketIndex(null);
                  return ok;
                }}
                isSelfTicket={!!ticket.isSelf}
                userEmail={ticket.isSelf ? ticket.email : undefined}
                nonMemberPrice={prices?.non_member}
                userId={ticket.user_id}
                company={ticket.company}
                title={ticket.title}
                email={ticket.email}
                askSelfTicketQuestion={
                  !ticket.isSelf &&
                  !selfTicketPresent &&
                  !ticket.selfPromptAnswered &&
                  ticket.first_name === PLACEHOLDER &&
                  ticket.last_name === PLACEHOLDER
                }
                onSelfTicketChoice={(isSelf) => handleSelfTicketChoice(index, isSelf)}
                isOpen={openTicketIndex === index}
                onToggleOpen={() =>
                  setOpenTicketIndex((prev) => (prev === index ? null : index))
                }
              />
            ))}
            <Button
              type="green"
              onClick={handleAddTicket}
              disabled={openTicketIndex != null}
            >
              <span className="plus">+</span> Add Another Ticket
            </Button>
          </>
        )}
      </div>
      <Totals total={total} tickets={completedTickets} />
      {hasCompletedTicket && isForYou > 0 && (
        <div className="form__add-to-cart">
          {addToCartRedirecting && redirectUrlRef.current && (
            <p className="form__add-to-cart-redirecting" aria-live="polite">
              Redirecting to Cart... {redirectCountdown}s{" "}
              <a href={redirectUrlRef.current}>Go now</a>
            </p>
          )}
          <Button
            type="button"
            onClick={handleAddToCart}
            loading={addToCartLoading}
            disabled={addToCartLoading || addToCartRedirecting}
          >
            Add to Cart
          </Button>
          {addToCartError && (
            <p className="form__add-to-cart-error" role="alert">
              {addToCartError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
